import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  TextField,
  TablePagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ShoppingCart as PlaceOrderIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  RemoveRedEye as PreviewIcon,
  FileDownload as ExportIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guestBlogOrderService, GuestBlogOrder } from '../services/guestBlogOrderService';
import { cartService } from '../services/cartService';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/common/RichTextEditor';
import HtmlContent from '../components/common/HtmlContent';
import { isHtmlEmpty } from '../utils/htmlSanitizer';
import DocumentPreviewModal from '../components/common/DocumentPreviewModal';
import { exportToCSV, formatDateForCSV, formatCurrencyForCSV } from '../utils/csvExport';
import ColumnVisibilityMenu from '../components/common/ColumnVisibilityMenu';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import {
  isAllowedContentDocumentFile,
  CONTENT_DOCUMENT_REJECT_MESSAGE,
} from '../utils/contentDocumentUpload';

const OrdersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState<number | undefined>(undefined);
  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<GuestBlogOrder | null>(null);
  const [editData, setEditData] = useState({ contentText: '', contentDocUrl: '', contentUrl: '' });
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [stripeOrderMonth, setStripeOrderMonth] = useState(new Date().getMonth() + 1);
  const [stripeOrderYear, setStripeOrderYear] = useState(new Date().getFullYear());
  const [stripeNotes, setStripeNotes] = useState('');
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  // Get user role from auth context
  const { user, isAdmin, isSuperAdmin, isClient } = useAuth();
  
  // Temporary fallback for admin detection
  const isSuperAdminFallback = user?.role === 'SUPER_ADMIN' || user?.email === 'superadmin@example.com';
  const isAdminFallback = isAdmin || isSuperAdminFallback;

  // Column visibility configuration - role-based
  const getDefaultColumns = () => {
    const baseColumns = [
      { id: 'select', label: 'Select', visible: true, required: true },
      { id: 'orderId', label: 'Order ID', visible: true, required: true },
      { id: 'siteUrl', label: 'Site URL', visible: true },
      { id: 'client', label: 'Client', visible: true },
      { id: 'project', label: 'Project', visible: true },
      { id: 'orderDate', label: 'Order Date', visible: true },
      { id: 'gbPrice', label: 'GB Price', visible: true },
      { id: 'liPrice', label: 'LI Price', visible: true },
      { id: 'totalPrice', label: 'Total Price', visible: true },
      { id: 'submissionType', label: 'Submission Type', visible: true },
      { id: 'status', label: 'Status', visible: true },
      { id: 'content', label: 'Content', visible: true },
      { id: 'actions', label: 'Actions', visible: true, required: true },
    ];

    if (isClient) {
      baseColumns.splice(12, 0,
        { id: 'payment', label: 'Payment', visible: true },
        { id: 'placement', label: 'Placement', visible: true },
      );
    }

    // Only Super Admin sees publisher name
    if (isSuperAdminFallback) {
      baseColumns.splice(6, 0, { id: 'publisherName', label: 'Publisher Name', visible: true });
    }

    return baseColumns;
  };

  const DEFAULT_COLUMNS = getDefaultColumns();

  const { columns, toggleColumn, resetDefaults, isColumnVisible } = useColumnVisibility(
    `guestBlogOrders_columnVisibility_${user?.role || 'guest'}`,
    DEFAULT_COLUMNS
  );

  const { data: filterOptionsData } = useQuery({
    queryKey: ['guestBlogOrdersFilterOptions', monthFilter, yearFilter, clientFilter],
    queryFn: () => guestBlogOrderService.getFilterOptions({
      status: 'COMPLETED',
      month: monthFilter,
      year: yearFilter,
      clientId: isClient ? user?.clientId : (clientFilter || undefined),
    }),
    enabled: isAdminFallback || isClient,
  });

  const clients = filterOptionsData?.data?.clients || [];
  const projects = filterOptionsData?.data?.projects || [];

  // Fetch orders
  const {
    data: ordersData,
    isLoading,
  } = useQuery({
    queryKey: ['guestBlogOrders', page, rowsPerPage, isClient ? user?.clientId : clientFilter, projectFilter, search, monthFilter, yearFilter, 'COMPLETED'],
    queryFn: () => guestBlogOrderService.getOrders({
      page: page + 1,
      limit: rowsPerPage,
      status: 'COMPLETED',
      clientId: isClient ? user?.clientId : (clientFilter || undefined),
      projectId: projectFilter || undefined,
      search: search || undefined,
      month: monthFilter,
      year: yearFilter,
    }),
    enabled: isAdminFallback || isClient, // Allow access for admins and clients
  });

  const orders = ordersData?.data || [];
  const totalCount = ordersData?.pagination?.total || 0;

  // Fetch all orders for stats calculation (without pagination)
  const { data: allOrdersData } = useQuery({
    queryKey: ['allGuestBlogOrders', isClient ? user?.clientId : clientFilter, projectFilter, search, monthFilter, yearFilter, 'COMPLETED'],
    queryFn: () => guestBlogOrderService.getOrders({
      page: 1,
      limit: 10000, // Large limit to get all orders
      status: 'COMPLETED',
      clientId: isClient ? user?.clientId : (clientFilter || undefined),
      projectId: projectFilter || undefined,
      search: search || undefined,
      month: monthFilter,
      year: yearFilter,
    }),
    enabled: isAdminFallback || isClient,
  });

  const allOrders = allOrdersData?.data || [];
  const selectedOrderRows = orders.filter((order) => selectedOrders.includes(order.id));

  // Update order mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      guestBlogOrderService.updateOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] });
      setEditDialogOpen(false);
      setSelectedOrder(null);
      setSnackbar({
        open: true,
        message: 'Order updated successfully!',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to update order.',
        severity: 'error',
      });
    },
  });

  // Delete order mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => guestBlogOrderService.deleteOrder(id),
    onSuccess: () => {
      // Invalidate orders cache
      queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] });
      // Invalidate ordered-sites cache so badges update on Guest Blog Sites page
      queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });
      // Invalidate guest-blog-sites cache to refresh usage statistics
      queryClient.invalidateQueries({ queryKey: ['guest-blog-sites'] });
      setSnackbar({
        open: true,
        message: 'Order deleted successfully!',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to delete order.',
        severity: 'error',
      });
    },
  });

  // Place bulk orders mutation
  const placeBulkMutation = useMutation({
    mutationFn: (orderIds: string[]) => guestBlogOrderService.placeBulkOrders(orderIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] });
      queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });
      setSelectedOrders([]);
      setSnackbar({
        open: true,
        message: `Successfully placed ${data.data.length} orders!`,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Failed to place orders.';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    },
  });

  // Event handlers
  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select orders that don't already have a placement (those are eligible
      // for the "Complete Placement" bulk action). Already-placed orders are
      // skipped so admins can't try to place them a second time.
      setSelectedOrders(
        orders
          .filter((order: any) => !order.linkedPlacement)
          .map((order) => order.id)
      );
    } else {
      setSelectedOrders([]);
    }
  };

  const handlePlaceBulkOrders = () => {
    if (selectedOrders.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select orders to place.',
        severity: 'error',
      });
      return;
    }

    if (isClient) {
      setStripeDialogOpen(true);
      return;
    }

    if (window.confirm(`Are you sure you want to place ${selectedOrders.length} orders?`)) {
      placeBulkMutation.mutate(selectedOrders);
    }
  };

  const handleStripeCheckoutForOrders = async () => {
    if (!selectedOrderRows.length) return;

    const uniqueProjectIds = Array.from(new Set(selectedOrderRows.map((o) => o.projectId)));
    if (uniqueProjectIds.length !== 1) {
      setSnackbar({
        open: true,
        message: 'Please select orders from a single project for Stripe checkout.',
        severity: 'warning',
      });
      return;
    }

    setStripeProcessing(true);
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('paymentStatus', 'success');
      currentUrl.searchParams.set('paymentSource', 'orders');
      const successUrl = currentUrl.toString();
      const cancelUrl = window.location.href;

      const response = await cartService.createOrdersStripeCheckoutSession({
        orderIds: selectedOrders,
        orderMonth: stripeOrderMonth,
        orderYear: stripeOrderYear,
        notes: stripeNotes.trim() || undefined,
        successUrl,
        cancelUrl,
      });

      if (!response?.data?.checkoutUrl) {
        throw new Error('Stripe checkout URL was not returned');
      }

      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || error?.message || 'Failed to start Stripe checkout.',
        severity: 'error',
      });
    } finally {
      setStripeProcessing(false);
    }
  };

  const handleViewOrder = (order: GuestBlogOrder) => {
    setSelectedOrder(order);
    setEditData({
      contentText: order.contentText || '',
      contentDocUrl: order.contentDocUrl || '',
      contentUrl: order.contentUrl || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditOrder = (order: GuestBlogOrder) => {
    setSelectedOrder(order);
    setEditData({
      contentText: order.contentText || '',
      contentDocUrl: order.contentDocUrl || '',
      contentUrl: order.contentUrl || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteOrder = (order: GuestBlogOrder) => {
    if (window.confirm(`Are you sure you want to delete order ${order.orderId}?`)) {
      deleteMutation.mutate(order.id);
    }
  };

  const handleUpdate = async () => {
    if (!selectedOrder) return;
    
    await updateMutation.mutateAsync({
      id: selectedOrder.id,
      data: {
        contentText: editData.contentText,
        contentUrl: editData.contentUrl,
        contentDocUrl: editData.contentDocUrl,
      },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedOrder) return;

    if (!isAllowedContentDocumentFile(file)) {
      setSnackbar({ open: true, message: CONTENT_DOCUMENT_REJECT_MESSAGE, severity: 'error' });
      event.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      const response = await guestBlogOrderService.uploadContentDocument(file);
      setEditData(prev => ({ ...prev, contentDocUrl: response.data.url }));
      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to upload file.',
        severity: 'error',
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClientFilterChange = (clientId: string) => {
    setClientFilter(clientId);
    setProjectFilter(''); // Clear project filter when client changes
  };

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      // Fetch ALL data with current filters (no pagination limit)
      const allDataResponse = await guestBlogOrderService.getOrders({
        limit: 10000, // Large limit to get all data
        clientId: clientFilter || undefined,
        projectId: projectFilter || undefined,
        search: search || undefined,
        month: monthFilter,
        year: yearFilter,
      });

      const allOrders = allDataResponse.data;

      if (allOrders.length === 0) {
        setSnackbar({
          open: true,
          message: 'No orders to export',
          severity: 'warning',
        });
        return;
      }

      const csvData = allOrders.map(order => {
        const orderDate = order.orderDate ? new Date(order.orderDate) : null;
        return {
          orderId: order.orderId || '',
          siteUrl: order.guestBlogSite?.site_url || '',
          client: order.client?.name || '',
          project: order.project?.projectName || '',
          submissionType: order.submissionType || '',
          price: formatCurrencyForCSV(order.price),
          basePrice: formatCurrencyForCSV(order.guestBlogSite?.base_price),
          liBasePrice: order.guestBlogSite?.li_base_price ? formatCurrencyForCSV(order.guestBlogSite.li_base_price) : 'N/A',
          status: order.status || '',
          month: orderDate ? orderDate.toLocaleString('en-US', { month: 'long' }) : '',
          year: orderDate ? orderDate.getFullYear().toString() : '',
          orderDate: formatDateForCSV(order.orderDate),
          createdAt: formatDateForCSV(order.createdAt),
        };
      });

      const columns = [
        { key: 'orderId' as const, label: 'Order ID' },
        { key: 'siteUrl' as const, label: 'Site URL' },
        { key: 'client' as const, label: 'Client' },
        { key: 'project' as const, label: 'Project' },
        { key: 'submissionType' as const, label: 'Submission Type' },
        { key: 'price' as const, label: 'Price' },
        { key: 'basePrice' as const, label: 'Base Price' },
        { key: 'liBasePrice' as const, label: 'LI Base Price' },
        { key: 'status' as const, label: 'Status' },
        { key: 'month' as const, label: 'Month' },
        { key: 'year' as const, label: 'Year' },
        { key: 'orderDate' as const, label: 'Order Date' },
        { key: 'createdAt' as const, label: 'Created At' },
      ];

      const filename = `orders_${new Date().toISOString().split('T')[0]}.csv`;
      exportToCSV(csvData, columns, filename);
      
      setSnackbar({
        open: true,
        message: `Exported ${allOrders.length} orders to CSV`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export orders',
        severity: 'error',
      });
    } finally {
      setExportingCSV(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    const paymentStatus = searchParams.get('paymentStatus');
    const paymentSource = searchParams.get('paymentSource');
    const paymentAttemptId = searchParams.get('paymentAttemptId');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus !== 'success' || paymentSource !== 'orders' || !paymentAttemptId || !sessionId) {
      return;
    }

    const finalizePayment = async () => {
      try {
        const response = await cartService.finalizeOrdersStripeCheckout({
          paymentAttemptId,
          sessionId,
        });

        queryClient.invalidateQueries({ queryKey: ['guestBlogOrders'] });
        queryClient.invalidateQueries({ queryKey: ['allGuestBlogOrders'] });
        queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });
        setSelectedOrders([]);
        setSnackbar({
          open: true,
          message: response.message || 'Payment completed and orders created successfully.',
          severity: 'success',
        });
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.error || 'Failed to finalize Stripe payment for orders.',
          severity: 'error',
        });
      } finally {
        const updated = new URLSearchParams(searchParams);
        updated.delete('paymentStatus');
        updated.delete('paymentSource');
        updated.delete('paymentAttemptId');
        updated.delete('session_id');
        updated.delete('paymentCancelled');
        setSearchParams(updated, { replace: true });
      }
    };

    void finalizePayment();
  }, [searchParams, setSearchParams, queryClient]);

  if (!isAdminFallback && !isClient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Access denied. Only Admins and Clients can view orders.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <Box>
          <Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: 'var(--t1)' }}>
            Guest Blog Orders
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: 'var(--t3)', mt: '2px', lineHeight: 1.5 }}>
            Track and manage all guest blog orders
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          {!isClient && selectedOrders.length > 0 ? (
            <Button
              onClick={handlePlaceBulkOrders}
              disabled={placeBulkMutation.isPending}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 13px',
                borderRadius: '6px',
                fontSize: '12.5px',
                fontWeight: 500,
                cursor: 'pointer',
                border: 'none',
                background: '#4F46E5',
                color: 'white',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                transition: 'all 0.14s',
                whiteSpace: 'nowrap',
                lineHeight: 1,
                flexShrink: 0,
                '&:hover': {
                  background: '#4338CA',
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
                },
                '&:disabled': {
                  background: '#9CA3AF',
                  cursor: 'not-allowed'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                <path d="M12 5v14M5 12h14"/>
              </svg>
              {isClient ? `Complete Order via Stripe (${selectedOrders.length})` : `Complete Order (${selectedOrders.length})`}
            </Button>
          ) : null}
          <ColumnVisibilityMenu
            columns={columns}
            onColumnToggle={toggleColumn}
            onResetDefaults={resetDefaults}
          />
          <Button
            onClick={handleExportCSV}
            disabled={exportingCSV || totalCount === 0}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '7px 13px',
              borderRadius: 'var(--r)',
              fontSize: '12.5px',
              fontWeight: 500,
              cursor: 'pointer',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--t2)',
              boxShadow: 'var(--shx)',
              transition: 'all 0.14s',
              whiteSpace: 'nowrap',
              lineHeight: 1,
              flexShrink: 0,
              '&:hover': {
                background: '#F9FAFB',
                color: 'var(--t1)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export as CSV
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--rl)', 
          padding: '14px 16px', 
          boxShadow: 'var(--shx)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          transition: 'box-shadow 0.18s, transform 0.18s', 
          cursor: 'default',
          '&:hover': { boxShadow: 'var(--shm)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0,
            background: 'var(--blue-l)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--blue)' }}>
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>
              {totalCount}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--t3)', marginTop: '3px' }}>
              Total Orders
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--rl)', 
          padding: '14px 16px', 
          boxShadow: 'var(--shx)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          transition: 'box-shadow 0.18s, transform 0.18s', 
          cursor: 'default',
          '&:hover': { boxShadow: 'var(--shm)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0,
            background: 'var(--green-l)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--green)' }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>
              {allOrders.filter(o => o.status === 'ACTIVE').length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--t3)', marginTop: '3px' }}>
              Live Orders
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--rl)', 
          padding: '14px 16px', 
          boxShadow: 'var(--shx)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          transition: 'box-shadow 0.18s, transform 0.18s', 
          cursor: 'default',
          '&:hover': { boxShadow: 'var(--shm)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0,
            background: 'var(--amber-l)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--amber)' }}>
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>
              {allOrders.filter(o => o.status === 'ACTIVE' || o.status === 'IN_PROGRESS').length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--t3)', marginTop: '3px' }}>
              Pending / In Review
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: 'var(--rl)', 
          padding: '14px 16px', 
          boxShadow: 'var(--shx)', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px', 
          transition: 'box-shadow 0.18s, transform 0.18s', 
          cursor: 'default',
          '&:hover': { boxShadow: 'var(--shm)', transform: 'translateY(-1px)' }
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '9px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0,
            background: 'var(--purple-l)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--purple)' }}>
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace', color: 'var(--t1)' }}>
              ${allOrders.reduce((s, o) => s + o.price, 0).toLocaleString()}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--t3)', marginTop: '3px' }}>
              Total Revenue
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Filter Bar */}
      <Box sx={{ 
        background: 'var(--surface)', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--rl)', 
        boxShadow: 'var(--shx)', 
        padding: '11px 14px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        flexWrap: 'wrap' 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', background: '#F9FAFB', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '6px 10px', minWidth: '230px', transition: 'all 0.15s' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--t4)', flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search by Site URL, Domain, Client, Project…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: 'none',
              background: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              fontSize: '12.5px',
              color: 'var(--t1)',
              width: '100%'
            }}
          />
        </Box>
        
        <Box sx={{ width: '1px', height: '18px', background: 'var(--border)', flexShrink: 0 }}></Box>
        
        <select
          value={monthFilter || ''}
          onChange={(e) => setMonthFilter(e.target.value ? Number(e.target.value) : undefined)}
          style={{
            appearance: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r)',
            padding: '5px 24px 5px 9px',
            fontFamily: 'inherit',
            fontSize: '12.5px',
            color: 'var(--t2)',
            background: '#F9FAFB',
            outline: 'none',
            cursor: 'pointer',
            transition: 'all 0.14s',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 7px center'
          }}
        >
          <option value="">All Months</option>
          <option value={1}>January</option>
          <option value={2}>February</option>
          <option value={3}>March</option>
          <option value={4}>April</option>
          <option value={5}>May</option>
          <option value={6}>June</option>
          <option value={7}>July</option>
          <option value={8}>August</option>
          <option value={9}>September</option>
          <option value={10}>October</option>
          <option value={11}>November</option>
          <option value={12}>December</option>
        </select>
        
        <select
          value={yearFilter || ''}
          onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : undefined)}
          style={{
            appearance: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r)',
            padding: '5px 24px 5px 9px',
            fontFamily: 'inherit',
            fontSize: '12.5px',
            color: 'var(--t2)',
            background: '#F9FAFB',
            outline: 'none',
            cursor: 'pointer',
            transition: 'all 0.14s',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 7px center'
          }}
        >
          <option value="">All Years</option>
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        
        {!isClient && (
          <select
            value={clientFilter}
            onChange={(e) => handleClientFilterChange(e.target.value)}
            style={{
              appearance: 'none',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r)',
              padding: '5px 24px 5px 9px',
              fontFamily: 'inherit',
              fontSize: '12.5px',
              color: 'var(--t2)',
              background: '#F9FAFB',
              outline: 'none',
              cursor: 'pointer',
              transition: 'all 0.14s',
              minWidth: '120px',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 7px center'
            }}
          >
            <option value="">Client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        )}
        
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          disabled={!clientFilter && !isClient}
          style={{
            appearance: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r)',
            padding: '5px 24px 5px 9px',
            fontFamily: 'inherit',
            fontSize: '12.5px',
            color: 'var(--t2)',
            background: '#F9FAFB',
            outline: 'none',
            cursor: 'pointer',
            transition: 'all 0.14s',
            minWidth: '145px',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 7px center'
          }}
        >
          <option value="">Project</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.projectName}
            </option>
          ))}
        </select>
        
        <Box sx={{ flex: 1 }}></Box>
        
        <button
          onClick={() => {
            setSearch('');
            setClientFilter('');
            setProjectFilter('');
            setMonthFilter(undefined);
            setYearFilter(undefined);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--t4)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: '4px 6px',
            borderRadius: 'var(--r)',
            transition: 'all 0.12s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'var(--red-l)';
            e.currentTarget.style.color = 'var(--red)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.color = 'var(--t4)';
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px' }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Clear
        </button>
      </Box>

      {/* Orders Table */}
      <Box sx={{ 
        background: 'var(--surface)', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--rl)', 
        boxShadow: 'var(--shx)', 
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '10px 16px', 
          borderBottom: '1px solid var(--border-sub)', 
          background: '#FAFAFA' 
        }}>
          <Typography sx={{ fontSize: '12.5px', color: 'var(--t3)' }}>
            Showing <b>{orders.length}</b> order{orders.length !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--t4)' }}>
            Sort by:
            <select style={{
              border: '1px solid var(--border)',
              borderRadius: '5px',
              padding: '4px 20px 4px 7px',
              fontFamily: 'inherit',
              fontSize: '12px',
              color: 'var(--t2)',
              background: '#fff',
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'10\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239CA3AF\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 5px center'
            }}>
              <option>Created (Newest)</option>
              <option>Total Price (High → Low)</option>
              <option>Order Month</option>
              <option>Status</option>
            </select>
          </Box>
        </Box>
        
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {!isClient && (
                <th style={{ 
                  textAlign: 'left', 
                  fontSize: '10.5px', 
                  fontWeight: 600, 
                  color: 'var(--t4)', 
                  letterSpacing: '0.5px', 
                  textTransform: 'uppercase', 
                  padding: '9px 14px', 
                  borderBottom: '1px solid var(--border)', 
                  background: '#FAFAFA', 
                  whiteSpace: 'nowrap', 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 10, 
                  transition: 'color 0.1s'
                }}>
                  {(() => {
                    const placeable = orders.filter((o: any) => !o.linkedPlacement);
                    const allSelected = placeable.length > 0 && selectedOrders.length === placeable.length;
                    return (
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          disabled={placeable.length === 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          style={{
                            width: '14px',
                            height: '14px',
                            border: allSelected ? '1.5px solid #3B82F6' : '1.5px solid #D1D5DB',
                            borderRadius: '3px',
                            cursor: placeable.length === 0 ? 'not-allowed' : 'pointer',
                            appearance: 'none',
                            background: allSelected ? '#3B82F6' : '#fff',
                            opacity: placeable.length === 0 ? 0.4 : 1,
                            transition: 'all 0.1s',
                            flexShrink: 0
                          }}
                        />
                        {allSelected && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px', position: 'absolute', pointerEvents: 'none' }}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </div>
                    );
                  })()}
                </th>
                )}
                <th style={{ 
                  textAlign: 'left', 
                  fontSize: '10.5px', 
                  fontWeight: 600, 
                  color: 'var(--t4)', 
                  letterSpacing: '0.5px', 
                  textTransform: 'uppercase', 
                  padding: '9px 14px', 
                  borderBottom: '1px solid var(--border)', 
                  background: '#FAFAFA', 
                  whiteSpace: 'nowrap', 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 10, 
                  transition: 'color 0.1s'
                }}>
                  Order ID
                  <svg className="sc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </th>
                {isColumnVisible('siteUrl') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s',
                    minWidth: '150px'
                  }}>
                    Site URL
                  </th>
                )}
                {isColumnVisible('client') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Client
                  </th>
                )}
                {isColumnVisible('project') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Project
                  </th>
                )}
                {isColumnVisible('orderDate') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Order Month
                  </th>
                )}
                {isColumnVisible('publisherName') && isSuperAdmin && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Publisher Name
                  </th>
                )}
                {isColumnVisible('gbPrice') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    GB Price
                    <svg className="sc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </th>
                )}
                {isColumnVisible('liPrice') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    LI Price
                  </th>
                )}
                {isColumnVisible('totalPrice') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Total Price
                    <svg className="sc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ display: 'inline', width: '9px', height: '9px', marginLeft: '3px', verticalAlign: 'middle', opacity: 0.35 }}>
                      <polyline points="18 15 12 9 6 15"/>
                    </svg>
                  </th>
                )}
                {isColumnVisible('submissionType') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Submission Type
                  </th>
                )}
                {isColumnVisible('status') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Status
                  </th>
                )}
                {isClient && isColumnVisible('payment') && (
                  <th style={{ 
                    textAlign: 'left',
                    fontSize: '10.5px',
                    fontWeight: 600,
                    color: 'var(--t4)',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    padding: '9px 14px',
                    borderBottom: '1px solid var(--border)',
                    background: '#FAFAFA',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    Payment
                  </th>
                )}
                {isClient && isColumnVisible('placement') && (
                  <th style={{ 
                    textAlign: 'left',
                    fontSize: '10.5px',
                    fontWeight: 600,
                    color: 'var(--t4)',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    padding: '9px 14px',
                    borderBottom: '1px solid var(--border)',
                    background: '#FAFAFA',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    Placement
                  </th>
                )}
                {isColumnVisible('content') && (
                  <th style={{ 
                    textAlign: 'left', 
                    fontSize: '10.5px', 
                    fontWeight: 600, 
                    color: 'var(--t4)', 
                    letterSpacing: '0.5px', 
                    textTransform: 'uppercase', 
                    padding: '9px 14px', 
                    borderBottom: '1px solid var(--border)', 
                    background: '#FAFAFA', 
                    whiteSpace: 'nowrap', 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10, 
                    transition: 'color 0.1s'
                  }}>
                    Content
                  </th>
                )}
                <th style={{ 
                  textAlign: 'left', 
                  fontSize: '10.5px', 
                  fontWeight: 600, 
                  color: 'var(--t4)', 
                  letterSpacing: '0.5px', 
                  textTransform: 'uppercase', 
                  padding: '9px 14px', 
                  borderBottom: '1px solid var(--border)', 
                  background: '#FAFAFA', 
                  whiteSpace: 'nowrap', 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 10, 
                  transition: 'color 0.1s'
                }}>
                  Created
                </th>
                <th style={{ 
                  textAlign: 'right', 
                  fontSize: '10.5px', 
                  fontWeight: 600, 
                  color: 'var(--t4)', 
                  letterSpacing: '0.5px', 
                  textTransform: 'uppercase', 
                  padding: '9px 16px 9px 14px', 
                  borderBottom: '1px solid var(--border)', 
                  background: '#FAFAFA', 
                  whiteSpace: 'nowrap', 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 10, 
                  transition: 'color 0.1s'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 14 : 13} align="center" style={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-sub)' }}>
                    Loading orders...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 14 : 13} align="center" style={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-sub)' }}>
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} style={{ transition: 'background 0.08s' }}>
                    {!isClient && (
                    <td style={{ padding: '10px 14px', fontSize: '12.5px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap', paddingLeft: '16px' }}>
                      {(order as any).linkedPlacement ? (
                        // Order already has a placement — show a "Placed" lock chip
                        // instead of a checkbox so it can't be selected for placement again.
                        <Tooltip title={`Already placed (${(order as any).linkedPlacement.placementId})`}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              background: 'var(--green-l, #ecfdf5)',
                              color: 'var(--green, #047857)',
                              border: '1px solid var(--green-border, #a7f3d0)',
                              fontSize: '10.5px',
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Placed
                          </span>
                        </Tooltip>
                      ) : (
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                            style={{
                              width: '14px',
                              height: '14px',
                              border: selectedOrders.includes(order.id) ? '1.5px solid #3B82F6' : '1.5px solid #D1D5DB',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              appearance: 'none',
                              background: selectedOrders.includes(order.id) ? '#3B82F6' : '#fff',
                              transition: 'all 0.1s',
                              flexShrink: 0
                            }}
                          />
                          {selectedOrders.includes(order.id) && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px', position: 'absolute', pointerEvents: 'none' }}>
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </div>
                      )}
                    </td>
                    )}
                    <td style={{ padding: '10px 14px', fontSize: '12.5px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', fontWeight: 500, color: 'var(--t4)' }}>
                        {order.orderId}
                      </span>
                    </td>
                    {isColumnVisible('siteUrl') && (
                      <td style={{ padding: '10px 14px', fontSize: '12.5px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <a 
                        href={order.guestBlogSite.site_url.startsWith('http') ? order.guestBlogSite.site_url : `https://${order.guestBlogSite.site_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'var(--blue)',
                          fontSize: '12.5px',
                          fontWeight: 450,
                          textDecoration: 'none',
                          display: 'block',
                          maxWidth: '155px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {order.guestBlogSite.site_url}
                      </a>
                      {isClient && (
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--t4)', marginTop: '4px' }}>
                          {order.orderId}
                        </div>
                      )}
                      </td>
                    )}
                    {isColumnVisible('client') && (
                      <td style={{ padding: '10px 14px', fontSize: '12.5px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--t1)', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                          {order.client.name}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('project') && (
                      <td style={{ padding: '10px 14px', fontSize: '12px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12px', color: 'var(--t2)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                          {order.project.projectName}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('orderDate') && (
                      <td style={{ padding: '10px 14px', fontSize: '12px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
                      </td>
                    )}
                    {isColumnVisible('publisherName') && isSuperAdmin && (
                      <td style={{ padding: '10px 14px', fontSize: '12.5px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {order.publishers?.publisherName || '—'}
                      </td>
                    )}
                    {isColumnVisible('gbPrice') && (
                      <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 600, color: 'var(--t1)', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: 600, color: 'var(--t1)' }}>
                          {guestBlogOrderService.formatPrice(order.guestBlogSite?.base_price || 0)}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('liPrice') && (
                      <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 600, color: 'var(--t1)', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: 600, color: 'var(--t1)' }}>
                          {guestBlogOrderService.formatPrice(order.guestBlogSite?.li_base_price || 0)}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('totalPrice') && (
                      <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 600, color: 'var(--t1)', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: 600, color: 'var(--t1)' }}>
                          {(() => {
                            let total = 0;
                            const multiplier = 1 + (order.client.percentage || 25) / 100;
                            if (order.submissionType === 'GBS' && order.guestBlogSite.base_price) {
                              total = order.guestBlogSite.base_price * multiplier;
                            } else if (order.submissionType === 'LI' && order.guestBlogSite.li_base_price) {
                              total = order.guestBlogSite.li_base_price * multiplier;
                            } else if (order.submissionType === 'BOTH') {
                              if (order.guestBlogSite.base_price) total += order.guestBlogSite.base_price * multiplier;
                              if (order.guestBlogSite.li_base_price) total += order.guestBlogSite.li_base_price * multiplier;
                            }
                            return guestBlogOrderService.formatPrice(total || order.price);
                          })()}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('submissionType') && (
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12px', color: 'var(--t2)' }}>
                          {order.submissionType}
                        </span>
                      </td>
                    )}
                    {isColumnVisible('status') && (
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          padding: '3px 9px', 
                          borderRadius: '99px', 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          whiteSpace: 'nowrap',
                          background: order.status === 'ACTIVE' ? 'var(--green-l)' : 
                                     order.status === 'IN_PROGRESS' ? 'var(--blue-l)' : 
                                     order.status === 'COMPLETED' ? 'var(--green-l)' : 
                                     order.status === 'CANCELLED' ? 'var(--red-l)' : 
                                     '#F3F4F6',
                          color: order.status === 'ACTIVE' ? 'var(--green)' : 
                                 order.status === 'IN_PROGRESS' ? 'var(--blue)' : 
                                 order.status === 'COMPLETED' ? 'var(--green)' : 
                                 order.status === 'CANCELLED' ? 'var(--red)' : 
                                 '#6B7280',
                          border: `1px solid ${order.status === 'ACTIVE' ? 'var(--green-b)' : 
                                 order.status === 'IN_PROGRESS' ? 'var(--blue-b)' : 
                                 order.status === 'COMPLETED' ? 'var(--green-b)' : 
                                 order.status === 'CANCELLED' ? 'var(--red-b)' : 
                                 '#E5E7EB'}`
                        }}>
                          <span style={{ content: '""', width: '5px', height: '5px', borderRadius: '50%', flexShrink: 0, background: order.status === 'ACTIVE' ? 'var(--green)' : 
                                     order.status === 'IN_PROGRESS' ? 'var(--blue)' : 
                                     order.status === 'COMPLETED' ? 'var(--green)' : 
                                     order.status === 'CANCELLED' ? 'var(--red)' : 
                                     '#6B7280'
                        }}></span>
                          {order.status}
                        </span>
                      </td>
                    )}
                    {isClient && isColumnVisible('payment') && (
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {order.paymentAttempt ? (
                          <div>
                            <span style={{ display: 'inline-flex', padding: '3px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, background: order.paymentAttempt.status === 'SUCCEEDED' ? 'var(--green-l)' : 'var(--blue-l)', color: order.paymentAttempt.status === 'SUCCEEDED' ? 'var(--green)' : 'var(--blue)', border: `1px solid ${order.paymentAttempt.status === 'SUCCEEDED' ? 'var(--green-b)' : 'var(--blue-b)'}` }}>
                              {order.paymentAttempt.status}
                            </span>
                            <div style={{ fontSize: '11px', color: 'var(--t4)', marginTop: '4px', fontFamily: 'JetBrains Mono, monospace' }}>
                              {order.paymentAttempt.providerPaymentIntentId || order.paymentAttempt.providerSessionId || order.paymentAttemptId}
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: 'var(--t4)' }}>Not paid yet</span>
                        )}
                      </td>
                    )}
                    {isClient && isColumnVisible('placement') && (
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {order.linkedPlacement ? (
                          <div>
                            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--t2)', fontWeight: 600 }}>
                              {order.linkedPlacement.placementId}
                            </span>
                            <div style={{ fontSize: '11px', color: 'var(--t4)', marginTop: '4px' }}>
                              {order.linkedPlacement.status}
                              {order.linkedPlacement.liveUrl ? ' - Live URL added' : ''}
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: 'var(--t4)' }}>Placement pending</span>
                        )}
                      </td>
                    )}
                    {isColumnVisible('content') && (
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {order.contentText || order.contentUrl || order.contentDocUrl ? (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          padding: '4px 10px', 
                          borderRadius: '99px', 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          background: '#D1FAE5', 
                          color: '#059669', 
                          border: '1px solid #A7F3D0' 
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px', flexShrink: 0 }}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Yes
                        </span>
                      ) : (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          padding: '4px 10px', 
                          borderRadius: '99px', 
                          fontSize: '11px', 
                          fontWeight: 600, 
                          background: '#F3F4F6', 
                          color: '#6B7280', 
                          border: '1px solid #E5E7EB' 
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '11px', height: '11px', flexShrink: 0 }}>
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                          No
                        </span>
                      )}
                      </td>
                    )}
                    <td style={{ padding: '10px 14px', fontSize: '11px', color: 'var(--t4)', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--t4)' }}>
                        {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-sub)', verticalAlign: 'middle', whiteSpace: 'nowrap', textAlign: 'right', paddingRight: '16px' }}>
                      <div style={{ display: 'inline-flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleViewOrder(order)}
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.1s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--blue-l)';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--blue)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'none';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--t4)';
                          }}
                          title="View"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: 'var(--t4)' }}>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>
                        {!isClient && (
                        <>
                        <button
                          onClick={() => handleEditOrder(order)}
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.1s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--amber-l)';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--amber)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'none';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--t4)';
                          }}
                          title="Edit"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: 'var(--t4)' }}>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order)}
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.1s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--red-l)';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--red)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'none';
                            const svg = e.currentTarget.querySelector('svg');
                            if (svg) svg.style.color = 'var(--t4)';
                          }}
                          title="Delete"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px', color: 'var(--t4)' }}>
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                        </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
            )}
            </tbody>
          </table>
        </Box>

        {/* Empty State */}
        {orders.length === 0 && !isLoading && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '72px 24px', 
            gap: '14px', 
            textAlign: 'center' 
          }}>
            <Box sx={{ 
              width: '60px', 
              height: '60px', 
              background: '#F3F4F6', 
              borderRadius: '16px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              marginBottom: '4px' 
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '26px', height: '26px', color: '#D1D5DB' }}>
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'var(--t1)', letterSpacing: '-0.2px' }}>
              No orders found
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'var(--t3)', maxWidth: '320px', lineHeight: 1.65 }}>
              No guest blog orders match your current filters. Try adjusting the month, year, or search terms.
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'var(--t4)' }}>
              Orders placed from the Guest Blog Sites page will appear here.
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sx={{
            borderTop: '1px solid var(--border-sub)',
            background: '#FAFAFA',
            '& .MuiTablePagination-toolbar': {
              padding: '9px 16px',
              minHeight: 'auto'
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '12px',
              color: 'var(--t2)'
            },
            '& .MuiTablePagination-select': {
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '5px',
              background: '#fff'
            },
            '& .MuiIconButton-root': {
              width: '26px',
              height: '26px',
              borderRadius: '5px',
              border: '1px solid var(--border)',
              background: '#fff',
              '&:hover:not(.disabled)': {
                background: '#F3F4F6'
              },
              '&.disabled': {
                opacity: 0.5,
                cursor: 'not-allowed'
              }
            }
          }}
        />
      </Box>

      <Dialog open={stripeDialogOpen} onClose={() => !stripeProcessing && setStripeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Placement with Stripe</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="info">
            You selected {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''}. Payment will be collected via Stripe before creating orders.
          </Alert>
          <TextField
            select
            label="Order Month"
            value={stripeOrderMonth}
            onChange={(e) => setStripeOrderMonth(Number(e.target.value))}
            SelectProps={{ native: true }}
            disabled={stripeProcessing}
          >
            <option value={1}>January</option>
            <option value={2}>February</option>
            <option value={3}>March</option>
            <option value={4}>April</option>
            <option value={5}>May</option>
            <option value={6}>June</option>
            <option value={7}>July</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>October</option>
            <option value={11}>November</option>
            <option value={12}>December</option>
          </TextField>
          <TextField
            select
            label="Order Year"
            value={stripeOrderYear}
            onChange={(e) => setStripeOrderYear(Number(e.target.value))}
            SelectProps={{ native: true }}
            disabled={stripeProcessing}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </TextField>
          <TextField
            label="Notes (optional)"
            multiline
            minRows={3}
            value={stripeNotes}
            onChange={(e) => setStripeNotes(e.target.value)}
            disabled={stripeProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStripeDialogOpen(false)} disabled={stripeProcessing}>Cancel</Button>
          <Button variant="contained" onClick={handleStripeCheckoutForOrders} disabled={stripeProcessing || selectedOrders.length === 0}>
            {stripeProcessing ? 'Redirecting...' : 'Proceed to Stripe'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        BackdropProps={{
          style: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s ease-out',
            '@keyframes modalSlideIn': {
              from: {
                opacity: 0,
                transform: 'scale(0.9)'
              },
              to: {
                opacity: 1,
                transform: 'scale(1)'
              }
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--t1)' }}>
              {isClient ? 'Order Details' : 'Edit Order'} - {selectedOrder?.orderId}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
              {isClient ? 'Review order status, payment, placement, and content details' : 'Update order details and content information'}
            </Typography>
          </Box>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s',
              flexShrink: 0
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {selectedOrder && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5, mb: 1 }}>
              <Box sx={{ p: 1.5, border: '1px solid var(--border-light)', borderRadius: '10px', background: 'var(--bg)' }}>
                <Typography sx={{ fontSize: '11px', color: 'var(--t4)', textTransform: 'uppercase', fontWeight: 700 }}>Order Status</Typography>
                <Typography sx={{ fontSize: '13px', color: 'var(--t1)', fontWeight: 700, mt: 0.5 }}>{selectedOrder.status}</Typography>
              </Box>
              <Box sx={{ p: 1.5, border: '1px solid var(--border-light)', borderRadius: '10px', background: 'var(--bg)' }}>
                <Typography sx={{ fontSize: '11px', color: 'var(--t4)', textTransform: 'uppercase', fontWeight: 700 }}>Payment</Typography>
                <Typography sx={{ fontSize: '13px', color: 'var(--t1)', fontWeight: 700, mt: 0.5 }}>{selectedOrder.paymentAttempt?.status || 'Not paid yet'}</Typography>
                {(selectedOrder.paymentAttempt?.providerPaymentIntentId || selectedOrder.paymentAttempt?.providerSessionId) && (
                  <Typography sx={{ fontSize: '11px', color: 'var(--t4)', mt: 0.5, fontFamily: 'JetBrains Mono, monospace' }}>
                    {selectedOrder.paymentAttempt.providerPaymentIntentId || selectedOrder.paymentAttempt.providerSessionId}
                  </Typography>
                )}
              </Box>
              <Box sx={{ p: 1.5, border: '1px solid var(--border-light)', borderRadius: '10px', background: 'var(--bg)' }}>
                <Typography sx={{ fontSize: '11px', color: 'var(--t4)', textTransform: 'uppercase', fontWeight: 700 }}>Placement</Typography>
                <Typography sx={{ fontSize: '13px', color: 'var(--t1)', fontWeight: 700, mt: 0.5 }}>
                  {selectedOrder.linkedPlacement?.placementId || 'Placement pending'}
                </Typography>
                {selectedOrder.linkedPlacement?.liveUrl && (
                  <Button component="a" href={selectedOrder.linkedPlacement.liveUrl} target="_blank" rel="noopener noreferrer" size="small" sx={{ mt: 0.5, p: 0, minWidth: 0 }}>
                    Open live URL
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Content Requirements - Rich Text Editor */}
          <Box sx={{ mb: 3 }}>
            <RichTextEditor
              value={editData.contentText}
              onChange={(value) => setEditData(prev => ({ ...prev, contentText: value }))}
              label="Content Requirements"
              placeholder="Enter content requirements, guidelines, keywords, or any specific instructions..."
              disabled={uploadingFile || isClient}
            />
          </Box>

          {/* Content Preview */}
          {!isHtmlEmpty(editData.contentText) && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Content Preview
              </Typography>
              <HtmlContent 
                content={editData.contentText} 
                maxHeight={200}
                sx={{ fontSize: '0.875rem' }}
              />
            </Box>
          )}

          {/* Content URL Section - Editable */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content URL (Google Docs, etc.)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://docs.google.com/document/d/..."
                value={editData.contentUrl}
                onChange={(e) => setEditData({ ...editData, contentUrl: e.target.value })}
                disabled={isClient}
              />
              {editData.contentUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<LinkIcon />}
                  component="a"
                  href={editData.contentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                </Button>
              )}
            </Box>
          </Box>

          {/* File Upload Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content Document
            </Typography>
            {editData.contentDocUrl ? (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Current file: {guestBlogOrderService.getFileName(editData.contentDocUrl)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<PreviewIcon />}
                  onClick={() => setPreviewModalOpen(true)}
                  variant="outlined"
                >
                  View Document
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => guestBlogOrderService.downloadFile(editData.contentDocUrl || '', `order-${selectedOrder?.orderId}-content`)}
                >
                  Download
                </Button>
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No document uploaded
                </Typography>
              </Box>
            )}
            {!isClient && (
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={uploadingFile}
            >
              {uploadingFile ? 'Uploading...' : 'Upload New File'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.txt,.rtf"
                onChange={handleFileUpload}
              />
            </Button>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          gap: '10px', 
          padding: '16px 26px', 
          borderTop: '1px solid var(--border-light)' 
        }}>
          <Button onClick={() => setEditDialogOpen(false)}>{isClient ? 'Close' : 'Cancel'}</Button>
          {!isClient && (
          <Button 
            onClick={handleUpdate} 
            variant="contained" 
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update Order'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Preview Modal */}
      {editData.contentDocUrl && (
        <DocumentPreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          documentUrl={editData.contentDocUrl}
          fileName={guestBlogOrderService.getFileName(editData.contentDocUrl)}
          onDownload={() => guestBlogOrderService.downloadFile(editData.contentDocUrl || '', `order-${selectedOrder?.orderId}-content`)}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersPage;